import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useGetChassisDropdownListQuery } from '../../api';
import { useLanguage } from '../../contexts/LanguageContext';

const normalizeText = (str) =>
    (str || '')
        .replace(/\s+/g, ' ')
        .replace(/\u00A0/g, ' ')
        .trim();

const formatLicensePlate = (plate) => {
    if (!plate) return '';
    return plate.replace(/\s+/g, '').toUpperCase();
};

const ChassisDropdown = ({ value, onChange, className, disabled, onResolvedSelected, showDescription = true }) => {
    const { getCurrentLanguageId, th } = useLanguage();
    const [selectedChassis, setSelectedChassis] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [filterValue, setFilterValue] = useState(''); // Custom filter input için
    const [specificSearch, setSpecificSearch] = useState('');
    const debounceTimerRef = useRef(null);

    const languageId = getCurrentLanguageId();

    const formattedSearchText = useMemo(() => {
        return formatLicensePlate(searchText || specificSearch);
    }, [searchText, specificSearch]);

    const { data: chassisList = [], isLoading: isLoadingChassis } = useGetChassisDropdownListQuery({
        RowCount: 200,
        SearchText: formattedSearchText,
        languageId: languageId
    });

    const chassisOptions = useMemo(() => {
        return chassisList.map((chassis) => {
            return {
                id: chassis.id,
                text: chassis.id,
                rawText: normalizeText(chassis.translatedText),
                licensePlate: chassis.licensePlate,
                fullData: chassis,
                searchableText: `${chassis.id} ${chassis.licensePlate || ''}`.toLowerCase()
            };
        });
    }, [chassisList]);

    const handleSelectionChange = (e) => {
        const selected = e.value;
        setSelectedChassis(selected);

        // Seçim yapıldığında filter'ı temizle
        setFilterValue('');
        setSearchText('');
        setSpecificSearch('');

        // Bekleyen timer varsa iptal et
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        const fullData = selected?.fullData;
        onChange(selected ? selected.id : '', fullData);
    };

    // Custom filter input onChange - Debounce ile API çağrısı
    const handleFilterInputChange = (value) => {
        // Input'ta hemen görünsün
        setFilterValue(value);

        // Specific search'i temizle
        if (value && specificSearch) {
            setSpecificSearch('');
        }

        // Önceki timer'ı iptal et
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // 500ms sonra API çağrısı yap
        debounceTimerRef.current = setTimeout(() => {
            setSearchText(value);
        }, 500);
    };

    // Custom filter template - Dropdown'ın kendi filter'ını değil, bizim controlled input'umuzu kullan
    const filterTemplate = () => (
        <div className="p-dropdown-filter-container">
            <InputText
                value={filterValue}
                onChange={(e) => handleFilterInputChange(e.target.value)}
                className="p-dropdown-filter w-full"
                placeholder={th('common.search')}
                autoFocus
                type="text"
                inputMode="text"
            />
            <span className="p-dropdown-filter-icon pi pi-search" />
        </div>
    );

    // Cleanup: Component unmount olduğunda timer'ı temizle
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!value) {
            if (specificSearch) setSpecificSearch('');
            return;
        }

        if (isLoadingChassis) return;

        const targetId = typeof value === 'string' ? value : value?.id;
        if (!targetId) return;

        const exists = chassisOptions.some((opt) => opt.id === targetId);

        if (!exists && chassisOptions.length > 0 && !specificSearch) {
            setSpecificSearch(targetId);
        } else if (exists && specificSearch && specificSearch !== targetId) {
            setSpecificSearch('');
        }
    }, [value, chassisOptions, isLoadingChassis, specificSearch]);

    useEffect(() => {
        if (!value || chassisOptions.length === 0) {
            setSelectedChassis(null);
            return;
        }

        const matched = chassisOptions.find((opt) => {
            if (typeof value === 'string') return opt.id === value;
            if (typeof value === 'object') return opt.id === value?.id;
            return false;
        });

        setSelectedChassis(matched || null);
    }, [value, chassisOptions]);

    useEffect(() => {
        if (selectedChassis && typeof onResolvedSelected === 'function') {
            onResolvedSelected(selectedChassis.fullData);
        }
    }, [selectedChassis, onResolvedSelected]);

    const itemTemplate = (opt) =>
        showDescription ? (
            <div className="flex flex-column">
                <span className="font-medium">{opt.id}</span>
                <small className="text-500">{opt.rawText}</small>
            </div>
        ) : (
            <span className="font-medium">{opt.id}</span>
        );

    const selectedItemTemplate = (opt) =>
        opt ? (
            showDescription ? (
                <span>
                    {opt.id} - {opt.rawText}
                </span>
            ) : (
                <span>{opt.id}</span>
            )
        ) : (
            <span className="text-500">{th('warrantyClaims.detail.form.chassisPlaceholder')}</span>
        );

    return (
        <Dropdown
            value={selectedChassis}
            options={chassisOptions}
            onChange={handleSelectionChange}
            optionLabel="text"
            dataKey="id"
            filter
            filterTemplate={filterTemplate}
            showClear
            className={className}
            placeholder={th('warrantyClaims.detail.form.chassisPlaceholder')}
            loading={isLoadingChassis}
            disabled={disabled}
            itemTemplate={itemTemplate}
            valueTemplate={selectedItemTemplate}
        />
    );
};

export default ChassisDropdown;